import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useCallback, useMemo } from "react";
import { useApiQuery } from "../../hooks/use-api-query";
import type { TestAgentV2Session } from "../../types/test-agent-2";
import { humanizeText } from "../../utils/text";
import { prettyPrintUrl } from "../../utils/url";
import { SessionCard } from "../session-card";
import type { IssueReport, TestCreatorDoneIntent } from "./details";

interface SessionDetailsPanelProps {
  sessionId: string | null;
  open: boolean;
  onClose: () => void;
}

export function SessionDetailsPanel({ sessionId, open, onClose }: SessionDetailsPanelProps) {
  const { data: session, isLoading } = useApiQuery<TestAgentV2Session>(
    `/vibe-qa/sessions/${sessionId}`,
    {
      enabled: !!sessionId && open,
      refetchInterval: (data) => {
        if (data.state.data?.data.status === "closed") {
          return false;
        }
        return 5000;
      },
    },
  );

  const lastDone = useMemo(() => {
    const maybeDone = session?.data?.last_done;
    if (!maybeDone) return null;
    return maybeDone as TestCreatorDoneIntent;
  }, [session]);

  const textForLlm = useCallback((issue: IssueReport) => {
    // Returns a markdown formatted text for LLM (vibe coding tool)
    // to fix the issue.
    let text = `### Issue: ${issue.title}\n`;
    text += `${issue.description}\n\n`;
    if (issue.severity) {
      text += `- Severity: ${humanizeText(issue.severity)}\n`;
    }
    if (issue.issue_type) {
      text += `- Type: ${humanizeText(issue.issue_type)}\n`;
    }
    if (issue.url) {
      text += `- URL: ${issue.url}\n`;
    }
    if (issue.help) {
      text += `- Help: ${issue.help}\n`;
    }
    if (issue.recommendations && issue.recommendations.length > 0) {
      text += `- Recommendations:\n`;
      issue.recommendations.forEach((rec) => {
        text += `  - ${rec}\n`;
      });
    }
    return text;
  }, []);

  const copyAllForLlm = useCallback(() => {
    if (!lastDone || lastDone.issues.length === 0) return;

    let text = `## QA Session Report\n\n`;
    text += `**Status:** ${humanizeText(lastDone.status)}\n`;
    text += `**Summary:** ${lastDone.message}\n\n`;
    if (lastDone.reasoning) {
      text += `**Reasoning:** ${lastDone.reasoning}\n\n`;
    }
    text += `---\n\n`;
    text += `## Issues Found (${lastDone.issues.length})\n\n`;

    lastDone.issues.forEach((issue, index) => {
      text += `${index + 1}. `;
      text += textForLlm(issue);
      text += `\n`;
    });

    navigator.clipboard.writeText(text);
    enqueueSnackbar("All issues copied to clipboard", {
      variant: "success",
      anchorOrigin: {
        horizontal: "right",
        vertical: "bottom",
      },
    });
  }, [lastDone, textForLlm]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 550, md: 700 },
          maxWidth: "100%",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h6" noWrap sx={{ flex: 1, minWidth: 0 }}>
              Session Details
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {isLoading && (
            <Stack alignItems="center" spacing={2} py={4}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading session...
              </Typography>
            </Stack>
          )}

          {sessionId && !isLoading && (
            <Stack spacing={2}>
              {/* Session Card with liveview/recording */}
              <SessionCard
                sessionId={sessionId}
                path="vibe-qa/sessions"
                eventsPath="vibe-qa/new-event"
              />

              <Divider />

              {/* Issue Reports */}
              <Card>
                <CardContent>
                  {lastDone ? (
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">{humanizeText(lastDone.status)}</Typography>
                        {lastDone.issues.length > 0 && (
                          <Button variant="contained" size="small" onClick={copyAllForLlm}>
                            Copy All for LLM
                          </Button>
                        )}
                      </Stack>
                      <Typography variant="body2">{lastDone.message}</Typography>
                      {lastDone.reasoning && (
                        <Typography variant="caption" color="text.secondary">
                          {lastDone.reasoning}
                        </Typography>
                      )}

                      <Divider />

                      {lastDone.issues.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No issues were found.
                        </Typography>
                      )}

                      {lastDone.issues.length > 0 && (
                        <Stack spacing={1}>
                          <Typography variant="subtitle2">
                            Issues Found ({lastDone.issues.length})
                          </Typography>
                          {lastDone.issues.map((issue, index) => (
                            <Card key={index} variant="outlined">
                              <CardContent>
                                <Stack direction="column" spacing={1}>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight="medium"
                                      noWrap
                                      sx={{ flex: 1, minWidth: 0 }}
                                    >
                                      {issue.title}
                                    </Typography>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      sx={{ minWidth: 100, ml: 1 }}
                                      onClick={() => {
                                        const text = textForLlm(issue);
                                        navigator.clipboard.writeText(text);
                                        enqueueSnackbar("Issue text copied to clipboard", {
                                          variant: "success",
                                          anchorOrigin: {
                                            horizontal: "right",
                                            vertical: "bottom",
                                          },
                                        });
                                      }}
                                    >
                                      Copy for LLM
                                    </Button>
                                  </Stack>
                                  <Typography variant="body2">{issue.description}</Typography>
                                  {issue.recommendations && issue.recommendations.length > 0 && (
                                    <Stack spacing={0.5}>
                                      <Typography variant="subtitle2">Recommendations:</Typography>
                                      {issue.recommendations.map((rec, recIndex) => (
                                        <Typography key={recIndex} sx={{ pl: 1 }} variant="body2">
                                          {recIndex + 1}. {rec}
                                        </Typography>
                                      ))}
                                    </Stack>
                                  )}
                                  {issue.help && (
                                    <Typography variant="body2" color="text.secondary">
                                      {issue.help}
                                    </Typography>
                                  )}
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    divider={<Typography variant="body2">·</Typography>}
                                  >
                                    {issue.severity && (
                                      <Typography variant="caption" color="text.secondary">
                                        {humanizeText(issue.severity)}
                                      </Typography>
                                    )}
                                    {issue.issue_type && (
                                      <Typography variant="caption" color="text.secondary">
                                        {humanizeText(issue.issue_type)}
                                      </Typography>
                                    )}
                                    {issue.url && (
                                      <Link
                                        href={issue.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="caption"
                                      >
                                        {prettyPrintUrl(issue.url)}
                                      </Link>
                                    )}
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {session?.data.status === "closed"
                        ? "Agent did not yield any results..."
                        : "Waiting for agent to complete..."}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
